import { useState } from "react";

import { visuallyHiddenStyles } from "@newsela/angelou";
import { remove } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import AnnotatedText from "@/view/AnnotatedText";
import ProseMirrorMarks from "@/view/ProseMirrorNode/ProseMirrorMarks";
import { TYPES as MARK_TYPES } from "@/view/ProseMirrorNode/ProseMirrorMarks/ProseMirrorMarks.constants";
import { annotationPropType } from "@/view/viewPropTypes";
import getTextChunksByParentElement from "./getTextChunksByParentElement";
import { anchorCss, wrapperCss } from "./AnnotationCardAnnotatedText.style";

const propTypes = {
  annotation: annotationPropType,
  focusContentBodyAnnotation: PropTypes.func,
  highlightedText: PropTypes.arrayOf(
    PropTypes.shape({
      endOffset: PropTypes.number.isRequired,
      identifier: PropTypes.string,
      parentIdentifier: PropTypes.string.isRequired,
      startOffset: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ),
  __cssFor: PropTypes.shape({
    root: PropTypes.arrayOf(PropTypes.object),
    AnnotatedText: {
      root: PropTypes.object,
    },
  }),
};

const renderAnnotatedText = ({
  annotation,
  isHovered,
  index,
  setIsHovered,
  textNode,
  __cssFor,
}) => {
  const { text, isFirstNode, isLastNode } = textNode;
  const textNodeAnnotation = {
    ...annotation,
    isStartInNode: isFirstNode,
    isEndInNode: isLastNode,
  };

  const renderText = () => (
    <AnnotatedText
      key={index}
      annotation={textNodeAnnotation}
      isActive={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      text={text}
      __cssFor={__cssFor}
    />
  );

  if (textNode.marks) {
    // Showing links in the highlighted text makes navigating challenging, because clicking on the link
    // will trigger the link rather than the navigation to the content body.
    const marksWithoutLinks = remove(
      [...textNode.marks],
      (mark) => mark.type === MARK_TYPES.link,
    );

    return (
      <ProseMirrorMarks node={textNode} marks={marksWithoutLinks}>
        {renderText()}
      </ProseMirrorMarks>
    );
  }

  return renderText();
};

// Will be modified in a separate ticket to account for overlapping annotations.
const AnnotationCardAnnotatedText = ({
  annotation,
  focusContentBodyAnnotation,
  highlightedText,
  __cssFor,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const chunks = getTextChunksByParentElement(highlightedText, annotation);

  const annotatedTextIsInteractive = !!focusContentBodyAnnotation;

  const onClick = (e) => {
    e.preventDefault();
    if (annotatedTextIsInteractive) {
      focusContentBodyAnnotation(annotation.id);
    }
  };

  return (
    <>
      {annotatedTextIsInteractive && (
        <a css={[visuallyHiddenStyles, anchorCss]} href="#" onClick={onClick}>
          Navigate back to annotation.
        </a>
      )}

      {chunks.map((chunk, index) => (
        // For the moment, we're rendering chunks of text within <p> rather than attempting to replicate
        // other markup (e.g. headings).
        <p key={index} css={[wrapperCss, __cssFor?.root]} onClick={onClick}>
          {chunk.map((textNode) =>
            renderAnnotatedText({
              annotation,
              isHovered,
              index,
              setIsHovered,
              textNode,
              __cssFor: __cssFor?.AnnotatedText,
            }),
          )}
        </p>
      ))}
    </>
  );
};

AnnotationCardAnnotatedText.propTypes = propTypes;

export default AnnotationCardAnnotatedText;
